import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REASONS } from "@/lib/status";
import { Loader2, Plus, Trash2, Package, ArrowLeft, Save } from "lucide-react";

export default function EditRequestPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [original, setOriginal] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [items, setItems] = useState([]);
  const [codeInput, setCodeInput] = useState("");
  const [qtyInput, setQtyInput] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/suppliers"),
          api.get(`/requests/${id}`),
        ]);
        setSuppliers(s.data);
        setOriginal(r.data);
        setSupplierId(r.data.supplier_id);
        setReason(r.data.reason);
        setInternalNote(r.data.internal_note || "");
        const its = (r.data.items && r.data.items.length > 0) ? r.data.items : [{
          product_code: r.data.product_code, product_name: r.data.product_name,
          quantity: r.data.quantity, unit: r.data.unit,
        }];
        setItems(its.map((it) => ({
          product_code: it.product_code,
          product_name: it.product_name,
          unit: it.unit || "adet",
          quantity: it.quantity,
          note: it.note || "",
        })));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
        navigate("/talepler");
      }
    })();
  }, [id, navigate]);

  const matchedProduct = useMemo(() => {
    const c = codeInput.trim().toUpperCase();
    if (!c) return null;
    return codeSuggestions.find((x) => x.code === c) || null;
  }, [codeInput, codeSuggestions]);

  // Server-side product search (debounced) — supports very large catalogs
  useEffect(() => {
    const c = codeInput.trim();
    if (!c) { setCodeSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: c, limit: 8 } });
        setCodeSuggestions(data);
      } catch { setCodeSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [codeInput]);

  const pickProduct = (p) => {
    setCodeInput(p.code);
    setSearchOpen(false);
    setActiveIdx(-1);
    setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
  };

  const addItem = async () => {
    setErrors({});
    const code = codeInput.trim().toUpperCase();
    if (!code) { setErrors({ code: "Ürün kodu girin" }); return; }
    let product = codeSuggestions.find((x) => x.code === code);
    if (!product) {
      try {
        const { data } = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
        product = data;
      } catch {
        setErrors({ code: "Geçersiz ürün kodu" }); return;
      }
    }
    const qty = Number(qtyInput);
    if (!qty || qty <= 0) { setErrors({ qty: "Miktar girin" }); return; }
    if (items.some((i) => i.product_code === product.code)) {
      setItems(items.map((i) => i.product_code === product.code ? { ...i, quantity: i.quantity + qty } : i));
    } else {
      setItems([...items, { product_code: product.code, product_name: product.name, unit: product.unit || "adet", quantity: qty, note: "" }]);
    }
    setCodeInput("");
    setQtyInput("");
    setSearchOpen(false);
    // Refocus the code field so the next product can be typed without clicking
    setTimeout(() => document.getElementById("edit-product-code-input")?.focus(), 0);
  };

  // Resolve strictly by EXACT code (never substitute a fuzzy suggestion) so a
  // fast typist never accidentally lands on an unrelated product.
  const confirmExactCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) { setErrors({ code: "Ürün kodu girin" }); return; }
    if (qtyInput && Number(qtyInput) > 0) { addItem(); return; }
    setErrors({});
    let product = codeSuggestions.find((x) => x.code === code);
    if (!product) {
      try {
        const { data } = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
        product = data;
      } catch {
        setErrors({ code: "Bu kodla birebir eşleşen ürün yok — listeden seçin." });
        return;
      }
    }
    setCodeInput(product.code);
    setSearchOpen(false);
    setActiveIdx(-1);
    setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
  };

  const handleCodeKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (codeSuggestions.length > 0) {
        setSearchOpen(true);
        setActiveIdx((i) => Math.min(i + 1, codeSuggestions.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Pick a suggestion ONLY when explicitly highlighted via arrow keys.
      if (searchOpen && activeIdx >= 0 && codeSuggestions[activeIdx]) {
        pickProduct(codeSuggestions[activeIdx]);
        return;
      }
      confirmExactCode();
    }
  };

  const removeItem = (code) => setItems(items.filter((i) => i.product_code !== code));
  const updateQty = (code, q) => setItems(items.map((i) => i.product_code === code ? { ...i, quantity: Number(q) || 0 } : i));
  const updateNote = (code, note) => setItems(items.map((i) => i.product_code === code ? { ...i, note } : i));

  const handleSave = async () => {
    if (!supplierId) { toast.error("Tedarikçi seçin"); return; }
    if (!reason) { toast.error("Talep sebebi seçin"); return; }
    if (items.length === 0) { toast.error("En az bir ürün ekleyin"); return; }
    setBusy(true);
    try {
      await api.patch(`/requests/${id}`, {
        items: items.map(({ product_code, quantity, note }) => ({ product_code, quantity, note: note || "" })),
        supplier_id: supplierId,
        reason,
        internal_note: internalNote,
      });
      toast.success("Talep güncellendi");
      navigate(`/talepler/${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!original) return <div className="text-slate-400">Yükleniyor...</div>;

  return (
    <div className="max-w-3xl mx-auto fade-up">
      <Link to={`/talepler/${id}`} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 gap-1">
        <ArrowLeft className="h-4 w-4" /> Talep Detayı
      </Link>

      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Talebi Düzenle</h1>
        <p className="text-sm text-slate-500 mt-1">
          <span className="font-medium text-slate-900">{original.request_no}</span> · {original.status === "Oluşturuldu" ? "Düzenlenebilir" : "Sadece yönetici düzenleyebilir"}
        </p>
      </div>

      {/* Header section */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 space-y-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="edit-select-supplier" className="mt-1.5 h-12 text-base">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Talep Sebebi</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="edit-select-reason" className="mt-1.5 h-12 text-base">
                <SelectValue placeholder="Sebep seçin" />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              İç Not <span className="text-slate-400 normal-case font-normal">(PDF'te görünmez)</span>
            </Label>
            <span data-testid="edit-note-char-count" className={`text-xs tabular-nums ${internalNote.length > 480 ? "text-amber-600 font-medium" : "text-slate-400"}`}>
              {internalNote.length}/500
            </span>
          </div>
          <Textarea
            data-testid="edit-internal-note"
            value={internalNote}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length <= 500) setInternalNote(v);
            }}
            maxLength={500}
            placeholder="Sadece sistem içi notlar..."
            className="text-base min-h-[80px]"
          />
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Package className="h-4 w-4" /> Ürünler</h2>

        <div className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-12 sm:col-span-6 relative">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Ürün Ara veya Kod Gir</Label>
            <Input
              data-testid="edit-input-product-code"
              id="edit-product-code-input"
              value={codeInput}
              onChange={(e) => { setCodeInput(e.target.value); setSearchOpen(true); setActiveIdx(-1); }}
              onFocus={() => { if (codeInput) setSearchOpen(true); }}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              onKeyDown={handleCodeKey}
              placeholder="Kod veya ürün adı yazın..."
              className="mt-1.5 h-12 sm:h-11 text-base"
              autoComplete="off"
            />
            {errors.code && <p className="text-xs text-rose-600 mt-1">{errors.code}</p>}
            {searchOpen && codeSuggestions.length > 0 && (
              <div data-testid="product-search-results" className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto thin-scroll">
                {codeSuggestions.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`product-result-${p.code}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickProduct(p)}
                    className={`w-full text-left px-3 min-h-[52px] py-2.5 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3 ${idx === activeIdx ? "bg-slate-50" : "hover:bg-slate-50"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 text-sm">{p.code}</div>
                      <div className="text-xs text-slate-500 truncate">{p.name}</div>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">{p.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-7 sm:col-span-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Miktar</Label>
            <Input
              id="qty-input"
              data-testid="edit-input-quantity"
              type="number"
              min="0"
              step="any"
              value={qtyInput}
              onChange={(e) => setQtyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
              placeholder="0"
              className="mt-1.5 h-12 sm:h-11 text-base"
            />
            {errors.qty && <p className="text-xs text-rose-600 mt-1">{errors.qty}</p>}
          </div>
          <div className="col-span-3 sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Birim</Label>
            <div className="mt-1.5 h-12 sm:h-11 px-3 flex items-center bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
              {matchedProduct?.unit || "—"}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Button data-testid="edit-add-item-btn" type="button" onClick={addItem} className="mt-1.5 w-full h-12 sm:h-11 bg-slate-900 hover:bg-slate-800 px-0">
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="mt-5">
          {items.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-8 border border-dashed border-slate-200 rounded-md">Henüz ürün eklenmedi</div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600">
                <div className="col-span-1">#</div>
                <div className="col-span-2">Kod</div>
                <div className="col-span-5">Açıklama</div>
                <div className="col-span-2 text-right">Miktar</div>
                <div className="col-span-1">Birim</div>
                <div className="col-span-1"></div>
              </div>
              {items.map((it, idx) => (
                <div key={it.product_code} data-testid={`edit-item-row-${it.product_code}`} className="grid grid-cols-12 gap-2 px-3 py-3 border-t border-slate-100 items-center">
                  <div className="col-span-1 text-xs text-slate-500">{idx + 1}</div>
                  <div className="col-span-11 sm:col-span-2 font-medium text-slate-900 text-sm break-words">{it.product_code}</div>
                  <div className="col-span-8 sm:col-span-5 text-sm text-slate-700 leading-snug break-words">{it.product_name}</div>
                  <div className="col-span-3 sm:col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={it.quantity}
                      onChange={(e) => updateQty(it.product_code, e.target.value)}
                      className="h-9 text-right"
                    />
                  </div>
                  <div className="col-span-1 text-xs text-slate-500">{it.unit}</div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={() => removeItem(it.product_code)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500" aria-label="Kaldır">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="col-span-12 sm:col-span-11 sm:col-start-2">
                    <Input
                      data-testid={`edit-item-note-${it.product_code}`}
                      value={it.note || ""}
                      onChange={(e) => updateNote(it.product_code, e.target.value)}
                      placeholder="Bu ürün için tedarikçiye not (opsiyonel)"
                      className="h-9 text-sm"
                      maxLength={200}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-5 flex gap-2">
          <Link to={`/talepler/${id}`} className="flex-1">
            <Button type="button" variant="outline" className="w-full h-12">Vazgeç</Button>
          </Link>
          <Button
            data-testid="edit-save-btn"
            type="button"
            disabled={busy || items.length === 0}
            onClick={handleSave}
            className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Değişiklikleri Kaydet</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
